import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Check, Film, Sparkles, Upload, Wand2, Zap } from 'lucide-react';
import './styles.css';

type JobState = 'idle' | 'uploading' | 'generating' | 'ready' | 'error';

const PROVIDER_MAX_VIDEO_SECONDS = 30;
const SAFE_TRIM_SECONDS = 29;

const presets = ['TikTok / Reels', 'Publicité produit', 'Unboxing', 'ASMR / food', 'Gaming', 'Trailer cinématique', 'Immobilier'];
const styles = ['Réaliste', 'Cinématique', 'ASMR', 'Dynamique'];

const useCases = [
  ['Publicités e-commerce', 'Transformez des vidéos produit silencieuses en clips premium avec sons de manipulation, transitions et ambiance studio.'],
  ['TikTok, Reels, Shorts', 'Ajoutez du relief sonore aux vidéos courtes pour capter l’attention dès les premières secondes.'],
  ['Unboxing', 'Générez cartons, papier froissé, tissu, objets, mouvements de caméra et micro-bruitages réalistes.'],
  ['Gaming et trailers', 'Créez impacts, whooshes, pas, magie, sci-fi, ambiances et sons cinématiques.'],
  ['ASMR / food', 'Produisez des textures précises : craquant, découpe, versement, froissements et sons de proximité.'],
  ['Immobilier', 'Ajoutez une ambiance propre à vos visites : portes, pas, extérieur, room tone et atmosphère discrète.']
];

const seoPages = [
  'générateur effets sonores IA',
  'générateur bruitage IA',
  'ajouter son vidéo IA',
  'sonoriser vidéo automatiquement',
  'vidéo en audio IA',
  'bruitage vidéo TikTok'
];

const beforeAfter = {
  original: 'https://sonorise-ai.pages.dev/api/uploaded/1cf946a2-c138-4a82-8226-aeeced37ee6f-demo-original-shoes.mp4',
  treated: 'https://sonorise-ai.pages.dev/api/uploaded/db42044d-5769-43cf-8b17-996fbf1aea75-demo-treated-shoes.mp4'
};

const pricing = [
  { id: 'free', name: 'Gratuit', price: '0 €', bullets: ['1 crédit / jour', 'Export avec filigrane', 'Clips courts', 'Aucune carte bancaire'] },
  { id: 'starter', name: 'Starter', price: '6,99 €', bullets: ['150 crédits / mois', 'MP4 jusqu’à 50 Mo', 'Export sans filigrane', 'Support standard'] },
  { id: 'creator', name: 'Créateur', price: '19,99 €', bullets: ['500 crédits / mois', 'Usage commercial', 'Génération rapide', 'Clips jusqu’à 60 sec'] },
  { id: 'studio', name: 'Studio', price: '59,99 €', bullets: ['2 500 crédits / mois', 'Upload jusqu’à 500 Mo', 'Priorité file d’attente', 'Support prioritaire'] }
] as const;

function getSessionId() {
  const key = 'sonorise_session_id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(key, created);
  return created;
}

function track(event: string, props: Record<string, unknown> = {}) {
  const payload = JSON.stringify({ event, sessionId: getSessionId(), props });
  if (navigator.sendBeacon) {
    navigator.sendBeacon('/api/events', new Blob([payload], { type: 'application/json' }));
    return;
  }
  fetch('/api/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body: payload, keepalive: true }).catch(() => undefined);
}

function readableSeconds(seconds: number) {
  return `${Math.round(seconds)} s`;
}

async function trimVideoForProvider(file: File, maxSeconds = SAFE_TRIM_SECONDS): Promise<File> {
  const sourceUrl = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.src = sourceUrl;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Impossible de lire la vidéo pour la raccourcir automatiquement.'));
    });

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    const scale = Math.min(1, 1280 / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(2, Math.round(width * scale));
    canvas.height = Math.max(2, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Impossible de préparer le raccourcissement vidéo.');

    const stream = canvas.captureStream(30);
    const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    const mimeType = candidates.find(type => MediaRecorder.isTypeSupported(type)) || '';
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 2_500_000 } : undefined);
    const chunks: BlobPart[] = [];
    recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };

    const draw = () => {
      if (video.paused || video.ended) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      requestAnimationFrame(draw);
    };

    await new Promise<void>((resolve, reject) => {
      recorder.onerror = () => reject(new Error('Le raccourcissement automatique a échoué.'));
      recorder.onstop = () => resolve();
      video.onended = () => { if (recorder.state !== 'inactive') recorder.stop(); };
      recorder.start(1000);
      video.currentTime = 0;
      video.play().then(() => {
        draw();
        window.setTimeout(() => {
          video.pause();
          if (recorder.state !== 'inactive') recorder.stop();
        }, maxSeconds * 1000);
      }).catch(() => reject(new Error('Le navigateur a bloqué le raccourcissement automatique. Réessayez après avoir sélectionné la vidéo.')));
    });

    const blob = new Blob(chunks, { type: mimeType || 'video/webm' });
    if (!blob.size) throw new Error('La vidéo raccourcie est vide.');
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'video';
    return new File([blob], `${baseName}-first-${maxSeconds}s.webm`, { type: blob.type || 'video/webm' });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

function App() {
  const [mode, setMode] = useState<'file' | 'url'>('file');
  const [preset, setPreset] = useState(presets[2]);
  const [style, setStyle] = useState(styles[0]);
  const [duration, setDuration] = useState('15');
  const [prompt, setPrompt] = useState('Déballage d’une paire de chaussures, papier froissé, boîte en carton, ambiance studio premium');
  const [negativePrompt, setNegativePrompt] = useState('voix, musique forte, distorsion');
  const [file, setFile] = useState<File | null>(null);
  const [fileDuration, setFileDuration] = useState<number | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [jobState, setJobState] = useState<JobState>('idle');
  const [variants, setVariants] = useState<string[]>([]);
  const [outputUrl, setOutputUrl] = useState('');
  const [taskId, setTaskId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [trimNotice, setTrimNotice] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(() => localStorage.getItem('sonorise_token') || '');
  const [account, setAccount] = useState<{ email: string; plan: string; credits: number } | null>(null);

  useEffect(() => {
    track('page_view', { path: window.location.pathname, source: document.referrer || 'direct' });
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch('/api/auth/me', { headers: { authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then((data: unknown) => { const typed = data as { account?: { email: string; plan: string; credits: number } } | null; if (typed?.account) setAccount(typed.account); })
      .catch(() => undefined);
  }, [token]);

  const credits = useMemo(() => {
    const seconds = Number(duration);
    if (seconds <= 15) return 1;
    if (seconds <= 30) return 2;
    return 4;
  }, [duration]);

  async function generateDemo() {
    track('demo_start', { mode, preset, style, duration, hasFile: Boolean(file), hasUrl: Boolean(videoUrl.trim()), credits });
    setJobState(mode === 'file' && file ? 'uploading' : 'generating');
    setVariants([]);
    setOutputUrl('');
    setTaskId('');
    setErrorMessage('');
    setTrimNotice('');
    try {
      if (!account) {
        throw new Error('Entrez votre email et cliquez sur “Obtenir 3 crédits gratuits” avant de générer.');
      }
      let publicVideoUrl = videoUrl.trim();
      if (mode === 'file') {
        if (!file) throw new Error('Aucun fichier sélectionné');
        if (fileDuration && Number(duration) > Math.ceil(fileDuration)) {
          throw new Error(`Cette vidéo dure environ ${Math.round(fileDuration)} s. Choisissez une durée égale ou plus courte.`);
        }
        let uploadFile = file;
        if (fileDuration && fileDuration > PROVIDER_MAX_VIDEO_SECONDS) {
          setJobState('uploading');
          setTrimNotice(`Votre vidéo dure ${readableSeconds(fileDuration)}. Pour respecter la limite IA, nous utilisons automatiquement les ${SAFE_TRIM_SECONDS} premières secondes.`);
          track('video_auto_trim_start', { originalSeconds: Math.round(fileDuration), trimToSeconds: SAFE_TRIM_SECONDS, size: file.size, type: file.type || 'unknown' });
          uploadFile = await trimVideoForProvider(file);
          track('video_auto_trim_complete', { originalSeconds: Math.round(fileDuration), trimToSeconds: SAFE_TRIM_SECONDS, trimmedSize: uploadFile.size, trimmedType: uploadFile.type || 'unknown' });
        }
        const form = new FormData();
        form.append('video', uploadFile);
        const uploadRes = await fetch('/api/upload-video', { method: 'POST', body: form });
        const uploadData = await uploadRes.json() as { videoUrl?: string; error?: string };
        if (!uploadRes.ok || !uploadData.videoUrl) throw new Error(uploadData.error || 'Upload impossible');
        publicVideoUrl = uploadData.videoUrl;
        track('upload_success', { mode, size: uploadFile.size, type: uploadFile.type || 'unknown', wasTrimmed: uploadFile !== file });
      }

      if (account.credits < credits) {
        throw new Error(`Crédits insuffisants. Il vous faut ${credits} crédit${credits > 1 ? 's' : ''} pour cette génération. Choisissez un forfait pour continuer.`);
      }

      track('generation_start', { mode, preset, style, duration, credits });
      setJobState('generating');
      const res = await fetch('/api/generate-audio', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ videoUrl: publicVideoUrl, prompt: `${preset}. ${style}. ${prompt.trim() || 'bruitages réalistes synchronisés avec les mouvements visibles, ambiance studio propre'}`, negativePrompt, duration, credits })
      });
      const data = await res.json() as { taskId?: string; error?: string; data?: unknown; account?: { email: string; plan: string; credits: number } };
      if (!res.ok) {
        if (res.status === 402) throw new Error(`Crédits insuffisants. Choisissez un forfait pour continuer.`);
        throw new Error(data.error || 'Generation failed');
      }
      if (data.account) setAccount(data.account);
      if (!data.taskId) throw new Error('Aucun taskId retourné');
      setTaskId(data.taskId);
      setVariants([`Tâche IA créée : ${data.taskId}`, 'Génération en cours chez le provider…']);

      const finalUrl = await pollTask(data.taskId);
      setOutputUrl(finalUrl);
      setVariants(['Vidéo sonorisée prête', 'Export MP4 généré par le modèle MMAudio', 'Vous pouvez prévisualiser ou télécharger le résultat.']);
      track('generation_complete', { taskId: data.taskId, duration, hasOutput: Boolean(finalUrl) });
      setJobState('ready');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue.';
      setErrorMessage(message);
      track('generation_error', { mode, duration, message });
      setJobState('error');
    }
  }

  async function signIn() {
    track('email_submit', { source: 'generator_account_box' });
    const res = await fetch('/api/auth/start', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email }) });
    const data = await res.json() as { token?: string; account?: { email: string; plan: string; credits: number }; error?: string };
    if (!res.ok || !data.token || !data.account) {
      alert(data.error || 'Connexion impossible');
      return;
    }
    localStorage.setItem('sonorise_token', data.token);
    setToken(data.token);
    setAccount(data.account);
    setErrorMessage('');
  }

  async function startCheckout(plan: 'starter' | 'creator' | 'studio') {
    track('checkout_start', { plan, source: 'pricing' });
    const checkoutEmail = account?.email || email;
    if (!checkoutEmail) {
      alert('Ajoutez votre email avant de choisir un forfait.');
      return;
    }
    const res = await fetch('/api/stripe/checkout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ plan, email: checkoutEmail })
    });
    const data = await res.json() as { url?: string; error?: string };
    if (!res.ok || !data.url) {
      alert(data.error || 'Checkout Stripe indisponible');
      return;
    }
    window.location.href = data.url;
  }

  async function pollTask(id: string) {
    for (let attempt = 0; attempt < 24; attempt += 1) {
      await new Promise(resolve => setTimeout(resolve, attempt === 0 ? 3500 : 7000));
      const res = await fetch(`/api/piapi-task/${encodeURIComponent(id)}`);
      const payload = await res.json() as { data?: { status?: string; output?: { video_url?: string; audio_url?: string; url?: string }; error?: { code?: number; message?: string } } };
      const data = payload.data;
      const status = data?.status || 'processing';
      const url = data?.output?.video_url || data?.output?.audio_url || data?.output?.url;
      setVariants([`Tâche IA : ${id}`, `Statut : ${status}`, 'Upload R2 → MMAudio → export final']);
      if (url) return url;
      if (status === 'failed') throw new Error(data?.error?.message || 'Génération échouée');
    }
    throw new Error('La génération prend trop de temps. Réessayez ou vérifiez le statut plus tard.');
  }

  return (
    <main>
      <nav className="nav wrap">
        <a className="logo" href="#top">Sonorise<span>.ai</span></a>
        <div className="navLinks">
          <a href="#outil">Outil IA</a>
          <a href="#cas">Cas d’usage</a>
          <a href="#tarifs">Tarifs</a>
          <a href="#seo">SEO</a>
        </div>
        <a className="navCta" href="#outil">Tester gratuitement</a>
      </nav>

      <section className="hero wrap" id="top">
        <div className="heroCopy">
          <div className="eyebrow"><Sparkles size={16}/> Vidéo → bruitages synchronisés par IA</div>
          <h1>Ajoutez automatiquement des bruitages réalistes à vos vidéos</h1>
          <p className="lede">Importez une vidéo muette, décrivez l’ambiance en français, et notre IA génère une bande-son synchronisée prête pour TikTok, Reels, YouTube Shorts ou vos publicités produit.</p>
          <div className="actions">
            <a className="button" href="#outil"><Wand2 size={18}/> Générer mon premier son</a>
            <a className="button secondary" href="#exemples">Voir un avant/après</a>
          </div>
          <div className="trust">
            <span><Check size={16}/> Interface française</span>
            <span><Check size={16}/> Export MP4</span>
            <span><Check size={16}/> Usage commercial</span>
            <span><Check size={16}/> Paiement en euros</span>
          </div>
        </div>
        <GeneratorCard
          mode={mode}
          setMode={setMode}
          prompt={prompt}
          setPrompt={setPrompt}
          negativePrompt={negativePrompt}
          setNegativePrompt={setNegativePrompt}
          preset={preset}
          setPreset={setPreset}
          style={style}
          setStyle={setStyle}
          duration={duration}
          setDuration={setDuration}
          file={file}
          setFile={setFile}
          fileDuration={fileDuration}
          setFileDuration={setFileDuration}
          videoUrl={videoUrl}
          setVideoUrl={setVideoUrl}
          credits={credits}
          jobState={jobState}
          generateDemo={generateDemo}
          variants={variants}
          outputUrl={outputUrl}
          taskId={taskId}
          errorMessage={errorMessage}
          trimNotice={trimNotice}
          email={email}
          setEmail={setEmail}
          account={account}
          signIn={signIn}
          startCheckout={startCheckout}
        />
      </section>

      <section className="strip wrap">
        <div><strong>Même logique que MMAudio</strong><span>upload, prompt, options, crédits, génération</span></div>
        <div><strong>Mais plus français</strong><span>prompts, SEO, tarifs, cas d’usage locaux</span></div>
        <div><strong>Plus précis</strong><span>sonorisation automatique de vidéos courtes</span></div>
      </section>

      <section className="section wrap" id="exemples">
        <Header eyebrow="Exemples" title="Des scènes que les créateurs comprennent tout de suite" copy="Le produit doit vendre le résultat : une vidéo qui sonne mieux, sans montage audio manuel." />
        <div className="beforeAfter">
          <article>
            <span>Avant · vidéo sans son IA</span>
            <video src={beforeAfter.original} controls playsInline preload="metadata" />
          </article>
          <article>
            <span>Après · bruitages générés par Sonorise.ai</span>
            <video src={beforeAfter.treated} controls playsInline preload="metadata" />
          </article>
        </div>
        <div className="demoProof">
          <p>Vidéo originale sans bruitage exploitable. Sonorise génère une piste synchronisée : carton, papier, mouvements et manipulation produit.</p>
          <a className="button" href="#outil" onClick={() => track('demo_cta_click', { source: 'before_after' })}><Wand2 size={18}/> Tester avec ma vidéo</a>
        </div>
        <div className="exampleGrid">
          {[
            ['Unboxing produit', 'Papier froissé, carton, tissu, manipulation douce, ambiance studio propre.'],
            ['TikTok dynamique', 'Whooshes courts, impact léger, mouvement caméra, transition punchy.'],
            ['Food / ASMR', 'Découpe croustillante, sauce versée, texture proche micro, ambiance calme.']
          ].map(([title, copy]) => <article className="card example" key={title}><div className="fakeVideo"><Film/></div><h3>{title}</h3><p>{copy}</p></article>)}
        </div>
      </section>

      <section className="section wrap" id="cas">
        <Header eyebrow="Marché français" title="Cas d’usage prioritaires" copy="On attaque les recherches long-tail où la douleur est claire : vidéos silencieuses, publicités produit, unboxing, Shorts." />
        <div className="grid3">
          {useCases.map(([title, copy]) => <article className="card" key={title}><h3>{title}</h3><p>{copy}</p></article>)}
        </div>
      </section>

      <section className="section wrap alt">
        <Header eyebrow="Workflow" title="Sonoriser une vidéo en 3 étapes" copy="Simple pour les créateurs, structuré pour devenir une vraie app payante." />
        <div className="steps">
          <article><b>01</b><h3>Importez</h3><p>MP4 ou URL vidéo, avec limites claires dès le premier écran.</p></article>
          <article><b>02</b><h3>Décrivez</h3><p>Prompt en français, prompt négatif, preset et style sonore.</p></article>
          <article><b>03</b><h3>Exportez</h3><p>Prévisualisez 3 variantes et téléchargez le MP4 final.</p></article>
        </div>
      </section>

      <section className="section wrap" id="tarifs">
        <Header eyebrow="Tarifs" title="Modèle en crédits, simple à comprendre" copy="Même mécanique que MMAudio, localisée en euros avec une promesse commerciale plus claire." />
        <div className="pricing">
          {pricing.map((plan, idx) => <article className={`card plan ${idx === 2 ? 'featured' : ''}`} key={plan.id}><h3>{plan.name}</h3><div className="price">{plan.price}<span>/mois</span></div><ul>{plan.bullets.map(b => <li key={b}>{b}</li>)}</ul>{plan.id === 'free' ? <a className="button small" href="#outil">Tester gratuitement</a> : <button className="button small" onClick={() => startCheckout(plan.id)}>Choisir</button>}</article>)}
        </div>
      </section>

      <section className="section wrap" id="seo">
        <Header eyebrow="SEO launch" title="Pages françaises à publier dès le départ" copy="Chaque page doit avoir un mini outil, des exemples avant/après, FAQ, et un CTA vers la génération." />
        <div className="pillGrid">{seoPages.map(page => <span key={page}>{page}</span>)}</div>
      </section>

      <section className="section wrap faq">
        <Header eyebrow="FAQ" title="Objections à traiter sur la landing" copy="Confiance, droits commerciaux, formats, et différence avec une banque de sons." />
        <div className="grid2">
          <article className="card"><h3>Puis-je écrire mes prompts en français ?</h3><p>Oui. L’interface, les exemples et les presets sont conçus pour les créateurs francophones.</p></article>
          <article className="card"><h3>Les sons sont-ils libres de droits ?</h3><p>Les exports payants incluent une licence d’utilisation commerciale selon nos conditions.</p></article>
          <article className="card"><h3>Est-ce une banque de sons ?</h3><p>Non. L’IA génère une piste personnalisée adaptée à votre vidéo, au lieu de chercher un son existant.</p></article>
          <article className="card"><h3>Pourquoi commencer en France seulement ?</h3><p>Pour concentrer le SEO, les exemples et le feedback produit avant d’ouvrir l’allemand.</p></article>
        </div>
      </section>

      <footer className="footer wrap">
        <div>© 2026 Sonorise.ai</div>
        <div>Confidentialité · Conditions · Licence commerciale · Contact</div>
      </footer>
    </main>
  );
}

function Header({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return <div className="sectionHead"><div className="eyebrow small"><Zap size={14}/>{eyebrow}</div><h2>{title}</h2><p>{copy}</p></div>;
}

function GeneratorCard(props: {
  mode: 'file' | 'url'; setMode: (mode: 'file' | 'url') => void; prompt: string; setPrompt: (v: string) => void;
  negativePrompt: string; setNegativePrompt: (v: string) => void; preset: string; setPreset: (v: string) => void; style: string; setStyle: (v: string) => void;
  duration: string; setDuration: (v: string) => void; file: File | null; setFile: (v: File | null) => void; fileDuration: number | null; setFileDuration: (v: number | null) => void; videoUrl: string; setVideoUrl: (v: string) => void;
  credits: number; jobState: JobState; generateDemo: () => void; variants: string[]; outputUrl: string; taskId: string; errorMessage: string;
  trimNotice: string;
  email: string; setEmail: (v: string) => void; account: { email: string; plan: string; credits: number } | null; signIn: () => void;
  startCheckout: (plan: 'starter' | 'creator' | 'studio') => void;
}) {
  const insufficientCredits = Boolean(props.account && props.account.credits < props.credits);
  return <div className="generator" id="outil">
    <div className="accountBox">
      {props.account ? <><strong>{props.account.email}</strong><span>{props.account.credits} crédits · plan {props.account.plan}</span></> : <><input placeholder="votre@email.fr" value={props.email} onChange={e => props.setEmail(e.target.value)} /><button onClick={props.signIn}>Obtenir 3 crédits gratuits</button></>}
    </div>
    <div className="tabs"><button className={props.mode === 'file' ? 'active' : ''} onClick={() => props.setMode('file')}>Télécharger un fichier</button><button className={props.mode === 'url' ? 'active' : ''} onClick={() => props.setMode('url')}>URL vidéo</button></div>
    {props.mode === 'file' ? <label className="drop"><Upload/><strong>{props.file ? props.file.name : 'Glissez une vidéo ici'}</strong><span>{props.file ? `${(props.file.size / 1024 / 1024).toFixed(1)} Mo sélectionnés${props.fileDuration ? ` · ${Math.round(props.fileDuration)} s` : ''}` : 'ou cliquez pour sélectionner un MP4 · max 50 Mo'}</span><input type="file" accept="video/mp4,video/webm,video/quicktime" hidden onChange={e => {
      const selected = e.target.files?.[0] || null;
      props.setFile(selected);
      props.setFileDuration(null);
      if (!selected) return;
      const url = URL.createObjectURL(selected);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const seconds = Number.isFinite(video.duration) ? video.duration : null;
        props.setFileDuration(seconds);
        if (seconds && Number(props.duration) > Math.ceil(seconds)) props.setDuration(String(Math.max(1, Math.floor(seconds))));
        URL.revokeObjectURL(url);
      };
      video.onerror = () => URL.revokeObjectURL(url);
      video.src = url;
    }} /></label> : <label className="field"><span>URL de la vidéo</span><input placeholder="https://..." value={props.videoUrl} onChange={e => props.setVideoUrl(e.target.value)} /></label>}
    <label className="field"><span>Description du son</span><textarea value={props.prompt} onChange={e => props.setPrompt(e.target.value)} /></label>
    <label className="field"><span>Sons à éviter</span><input value={props.negativePrompt} onChange={e => props.setNegativePrompt(e.target.value)} /></label>
    <div className="fieldRow"><label className="field"><span>Preset</span><select value={props.preset} onChange={e => props.setPreset(e.target.value)}>{presets.map(p => <option key={p}>{p}</option>)}</select></label><label className="field"><span>Style</span><select value={props.style} onChange={e => props.setStyle(e.target.value)}>{styles.map(s => <option key={s}>{s}</option>)}</select></label></div>
    <div className="fieldRow"><label className="field"><span>Durée</span><select value={props.duration} onChange={e => props.setDuration(e.target.value)}><option value="8">8 secondes</option><option value="15">15 secondes</option><option value="30">30 secondes</option></select></label><div className="credits"><span>Crédits requis</span><strong>{props.credits}</strong></div></div>
    {props.fileDuration && props.fileDuration > PROVIDER_MAX_VIDEO_SECONDS ? <div className="authNotice">Vidéo longue détectée : nous utiliserons automatiquement les {SAFE_TRIM_SECONDS} premières secondes pour la génération IA. Votre fichier original n’est pas modifié.</div> : null}
    {props.trimNotice ? <div className="authNotice">{props.trimNotice}</div> : null}
    {!props.account ? <div className="authNotice">Entrez votre email pour recevoir 3 crédits gratuits avant de lancer une génération réelle.</div> : null}
    {insufficientCredits ? <div className="upgradeNotice"><strong>Crédits insuffisants</strong><span>Il vous faut {props.credits} crédit{props.credits > 1 ? 's' : ''}. Ajoutez un forfait pour générer cette vidéo.</span><div><button onClick={() => props.startCheckout('starter')}>Starter 6,99 €</button><button onClick={() => props.startCheckout('creator')}>Créateur 19,99 €</button><a href="#tarifs">Voir les forfaits</a></div></div> : null}
    <button className="button full" onClick={props.generateDemo} disabled={props.jobState === 'generating'}>{props.jobState === 'generating' ? 'Génération en cours…' : insufficientCredits ? 'Acheter des crédits pour générer' : 'Générer le son'}</button>
    <div className={`result ${props.jobState}`}>
      {props.jobState === 'idle' && 'Votre vidéo sonorisée apparaîtra ici.'}
      {props.jobState === 'uploading' && 'Upload de la vidéo vers Cloudflare R2…'}
      {props.jobState === 'generating' && 'Analyse de la vidéo, création des bruitages et préparation des variantes…'}
      {props.jobState === 'error' && <><div>{props.errorMessage || 'Erreur de génération. Vérifiez la vidéo ou la configuration provider/API.'}</div>{props.errorMessage.toLowerCase().includes('crédit') ? <div className="resultActions"><button onClick={() => props.startCheckout('starter')}>Acheter Starter</button><button onClick={() => props.startCheckout('creator')}>Acheter Créateur</button><a href="#tarifs">Comparer les forfaits</a></div> : null}</>}
      {props.jobState === 'ready' && <>
        {props.outputUrl ? <div className="outputPreview"><video src={props.outputUrl} controls playsInline /><a className="button small" href={props.outputUrl} target="_blank" rel="noreferrer">Télécharger le MP4</a></div> : null}
        {props.variants.map((v, i) => <div className="variant" key={v}><b>{i === 0 ? 'Résultat' : `Info ${i}`}</b><span>{v}</span>{props.outputUrl && i === 0 ? <a href={props.outputUrl} target="_blank" rel="noreferrer">Ouvrir</a> : null}</div>)}
        {props.taskId ? <small className="taskId">Task ID: {props.taskId}</small> : null}
      </>}
    </div>
  </div>;
}

createRoot(document.getElementById('root')!).render(<App />);
